import React from 'react';
import PropTypes from 'prop-types';
import { useWalletCardLogic } from './WalletCardLogic';
import Card from '../../layout/Card/Card';
import Section from '../../layout/Section/Section';
import FormRow from '../../form/FormRow/FormRow';
import FormColumn from '../../form/FormColumn/FormColumn';
import NumericField from '../../form/NumericField/NumericField';
import SignalTable from './SignalTable/SignalTable';
import ToggleSwitch from '../../form/ToggleSwitch/ToggleSwitch'
import SelectField from '../../form/SelectField/SelectField'
import IntermediateTable from '../../shared/IntermediateTable/IntermediateTable';
import ProposalHiddenField from './ProposalHidden/ProposalHidden'
import { LIMITES, OPTIONS, CARTA_DE_CREDITO_TOOLTIP } from '../../../utils/constantes';

const WalletCard = ({setWalletData, simulationResult, isMRV, pagadoria, parcelamentoMaximoPropostaIdeal}) => {

  const maxParcelas = typeof parcelamentoMaximoPropostaIdeal === 'number' ? parcelamentoMaximoPropostaIdeal : WalletCard.defaultProps.parcelamentoMaximoPropostaIdeal;

  const minSignalValue = (() => {
    const params = simulationResult?.propostaRequest?.parametrosProposta?.parametrosRatingSelecionados;
    const valorImovel = simulationResult?.propostaRequest?.unidade?.valorImovelSemDeltaSemDescontos;
    if (params?.porcentagemSinalMinimo != null && valorImovel != null) {
      return params.porcentagemSinalMinimo * valorImovel;
    }
    return null;
  })();

  const {
    rating,
    balaDePrata,
    entradaFacilitada,
    cartaDeCredito,
    planoFacil,
    monthlyInstallmentValue,
    monthlyInstallmentsCount,
    couponValue,
    signals,
    atoComplementar,
    intermediates,
    handleMonthlyInstallmentValueChange,
    handleMonthlyInstallmentsCountChange,
    handleCouponValueChange,
    handleSignalsChange,
    handleRatingChange,
    handleBalaDePrataChange,
    handleEntradaFacilitadaChange,
    handleCartaDeCreditoChange,
    handleplanoFacilChange,
    handleIntermediatesChange,
    handleProposalIdChange,
  } = useWalletCardLogic(setWalletData, simulationResult);

  return (
    <Card title={`Carteira ${isMRV ? 'MRV' : 'Sensia'}`}>
      <FormRow>
        <FormColumn cols={3}>
          <NumericField
            id="valorParcelaMensal"
            label="Valor da Parcela Mensal (R$)"
            value={monthlyInstallmentValue}
            onChange={handleMonthlyInstallmentValueChange}
            currency={true}
            disabled={true}
          />
        </FormColumn>
        <FormColumn cols={3}>
          <NumericField
            id="quantidadeParcelasMensais"
            label="Qtd de Parcelas Mensais"
            value={monthlyInstallmentsCount}
            onChange={handleMonthlyInstallmentsCountChange}
            min="1"
            max={balaDePrata ? maxParcelas : simulationResult?.propostaRequest.parametrosProposta.numeroMaximoParcelas}
            tooltip={`O valor deve estar entre 1 e ${balaDePrata ? maxParcelas : simulationResult?.propostaRequest.parametrosProposta.numeroMaximoParcelas}\n
                      EmCash tem possibilidade de 60x`}
          />
        </FormColumn>
        <FormColumn equal5>
          <SelectField
            id="rating"
            label="Rating"
            value={rating}
            onChange={handleRatingChange}
            options={OPTIONS.RATING}
          />
        </FormColumn>
        <FormColumn cols={3}>
          <NumericField
            id="valorCupom"
            label="Valor Cupom (R$)"
            value={couponValue}
            onChange={handleCouponValueChange}
            currency={true}
          />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn cols={3}>
          <SelectField
            id="planoFacil"
            label="Tipo de Plano"
            value={planoFacil}
            onChange={handleplanoFacilChange}
            options={OPTIONS.PLANO}
          />
        </FormColumn>
        <FormColumn equal5>
          <ToggleSwitch
            style={{ marginTop: 35 }}
            id="balaDePrata"
            label="Bala de prata"
            isChecked={balaDePrata}
            onChange={handleBalaDePrataChange}
            disabled={entradaFacilitada}
          />
        </FormColumn>
        <FormColumn equal5>
          <ToggleSwitch
            style={{ marginTop: 35 }}
            id="entradaFacilitada"
            label="Entrada Facilitada"
            isChecked={entradaFacilitada}
            onChange={handleEntradaFacilitadaChange}
            disabled={balaDePrata}
          />
        </FormColumn>
        <FormColumn equal5>
          <ToggleSwitch
            style={{ marginTop: 35 }}
            id="cartaDeCredito"
            label="Carta de Crédito"
            isChecked={cartaDeCredito}
            onChange={handleCartaDeCreditoChange}
            disabled={pagadoria}
            tooltip={CARTA_DE_CREDITO_TOOLTIP}
          />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn cols={6}>
          <Section title="Parcelas de Sinal" className="mt-4">
            <SignalTable
              onSignalsChange={handleSignalsChange}
              initialSignals={signals}
              pagadoria={pagadoria}
              entradaFacilitada={entradaFacilitada}
              minSignalValue={minSignalValue}
              cartaDeCredito={cartaDeCredito}
              atoComplementar={atoComplementar}
            />
          </Section>
        </FormColumn>
        <FormColumn cols={6}>
          <Section title="Parcelas Intermediárias" className="mt-4">
            <IntermediateTable
              onIntermediatesChange={handleIntermediatesChange}
              initialIntermediates={intermediates}
              maxLimit={LIMITES.INTERMEDIARIAS}
              disableValuesExceptFirst={true}
            />
          </Section>
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn>
          <ProposalHiddenField onValueChange={handleProposalIdChange} />
        </FormColumn>      </FormRow>
    </Card>
  );
};

WalletCard.propTypes = {
  setWalletData: PropTypes.func.isRequired,
  simulationResult: PropTypes.shape({
    propostaRequest: PropTypes.shape({
      parametrosProposta: PropTypes.shape({
        numeroMaximoParcelas: PropTypes.number,
        parametrosRatingSelecionados: PropTypes.shape({
          porcentagemSinalMinimo: PropTypes.number
        })
      }),
      unidade: PropTypes.shape({
        valorImovelSemDeltaSemDescontos: PropTypes.number
      })
    })
  }),
  isMRV: PropTypes.bool,
  pagadoria: PropTypes.bool,
  parcelamentoMaximoPropostaIdeal: PropTypes.number
};

WalletCard.defaultProps = {
  isMRV: false,
  pagadoria: false,
  parcelamentoMaximoPropostaIdeal: 50,
  simulationResult: {
    propostaRequest: {
      parametrosProposta: {
        numeroMaximoParcelas: 50
      }
    }
  }
};

export default WalletCard;